import React from 'react';
import ReactDOM from 'react-dom/client';
import { env } from '@/core/config/env';
// Side-effect imports - these initialize when imported
import { featureRegistry } from '@/core/feature-registry';
import '@/core/i18n/i18n';
import { api } from '@/core/api/apiClient';
import { eventBus } from '@/core/events/event-bus';

// Mark as used for side effects
void featureRegistry;
void api;
void eventBus;

// ═══════════════════════════════════════════════════════════════════════════════
// CRITICAL PERFORMANCE: Lazy import socket and tracking
// These are imported dynamically to avoid blocking the initial render
// socket.io-client (~50KB) and tracking code are loaded AFTER first paint
// ═══════════════════════════════════════════════════════════════════════════════

import App from '@/App';

/**
 * Defer non-critical initialization to after first paint
 * Uses requestIdleCallback with fallback to setTimeout
 */
function deferToIdle(callback: () => void, timeout = 2000): void {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, 100);
  }
}

/**
 * Initialize socket and tracking after the app has rendered
 * This prevents blocking the first paint
 */
async function initializeDeferredSystems(): Promise<void> {
  try {
    // Dynamic import socket.io - only loads when needed
    const { initializeSocket } = await import('@/core/realtime/socket');
    initializeSocket();
    
    // Dynamic import tracking - only loads when needed
    const { initializeTelemetry } = await import('@/core/tracking/telemetry');
    const { track } = await import('@/core/tracking/tracking');
    
    initializeTelemetry();
    track('AppInitialized', {
      timestamp: Date.now(),
    });
  } catch (error) {
    console.error('Failed to initialize deferred systems:', error);
  }
}

/**
 * Kernel - Main initialization point for the frontend application
 * Initializes all core systems in the correct order
 */
export async function initializeKernel(): Promise<void> {
  try {
    // 1. Validate environment
    if (!env.apiUrl) {
      throw new Error('API URL is not configured');
    }

    // 2. Register Features (features can register themselves on import)
    // Features should call featureRegistry.register() when imported
    
    // 3. Initialize i18n (already done via import)
    // i18n is initialized when the module is imported
    
    // 4. Initialize API Client (already done via import)
    // apiClient is initialized when the module is imported
    
    // 5. Initialize EventBus (already done via import)
    // eventBus is initialized when the module is imported

    // 6. Render App FIRST (with QueryClientProvider for TanStack Query)
    // This ensures the UI is visible before we load socket.io and tracking
    if (typeof window !== 'undefined') {
      const rootElement = document.getElementById('root');
      if (!rootElement) {
        throw new Error('Root element not found');
      }

      const root = ReactDOM.createRoot(rootElement);
      root.render(
        <React.StrictMode>
          <App />
        </React.StrictMode>
      );
      
      // 7. DEFER: Initialize Socket and Tracking AFTER first paint
      // This prevents socket.io (~50KB) and tracking from blocking the render
      deferToIdle(() => {
        initializeDeferredSystems();
      });
    }
  } catch (error) {
    console.error('Failed to initialize kernel:', error);
    // Show error to user
    if (typeof window !== 'undefined') {
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif;">
            <div style="text-align: center;">
              <h1>Application Error</h1>
              <p>Failed to initialize application. Please refresh the page.</p>
              <button onclick="window.location.reload()" style="padding: 10px 20px; margin-top: 20px; cursor: pointer;">
                Refresh Page
              </button>
            </div>
          </div>
        `;
      }
    }
    throw error;
  }
}

